import { useCallback, useEffect, useRef, useState } from "react";
import { Sparkles, RefreshCw, Trophy, TrendingUp, Target } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";

const ICONS = [Trophy, TrendingUp, Target];

export const AiSummary = ({ hotel, total, onUpdated }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const cb = useRef(onUpdated);
  cb.current = onUpdated;

  const generate = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.post("/analytics/summary", null, { params: { hotel } });
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, [hotel]);

  useEffect(() => {
    let live = true;
    setData(null);
    api.get("/analytics/summary", { params: { hotel } }).then((r) => {
      if (!live) return;
      if (r.data.bullets?.length) setData(r.data);
      else if (!r.data.updating) generate();
      else setData(r.data);
    });
    const t = setInterval(() => {
      api.get("/analytics/summary", { params: { hotel } }).then((r) => {
        if (!live) return;
        setData((d) => {
          if (d?.generated_at && r.data.generated_at && r.data.generated_at !== d.generated_at) cb.current?.();
          else if (r.data.updating && !d?.updating) cb.current?.();
          return r.data.bullets?.length ? r.data : (d ? { ...d, updating: r.data.updating } : d);
        });
      }).catch(() => {});
    }, 15000);
    return () => { live = false; clearInterval(t); };
  }, [hotel, generate]);

  return (
    <section data-testid="ai-summary-card" className="relative overflow-hidden rounded-3xl bg-emerald-950 text-amber-50 p-6 sm:p-8 shadow-2xl shadow-emerald-950/30 rise">
      <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-amber-400/20 blur-3xl" />
      <div className="absolute -left-10 bottom-0 h-40 w-40 rounded-full bg-emerald-400/20 blur-3xl" />
      <div className="relative flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-amber-300 text-[11px] font-semibold uppercase tracking-[0.2em]"><Sparkles className="h-4 w-4" /> Management overview · AI</div>
          <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight">{hotel === "All" ? "Across all properties" : hotel}</h2>
        </div>
        <button data-testid="ai-summary-regenerate-button" onClick={generate} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-amber-300/40 px-4 py-2 text-sm font-semibold text-amber-200 hover:bg-amber-300/10 transition-colors disabled:opacity-50">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Analysing…" : "Regenerate"}
        </button>
      </div>
      <div className="relative mt-6 grid md:grid-cols-3 gap-4">
        {(busy && !data ? [0, 1, 2] : data?.bullets || []).map((b, i) => {
          const Icon = ICONS[i] || Sparkles;
          if (typeof b === "number") return <div key={b} className="h-32 rounded-2xl shimmer" />;
          return (
            <div key={i} data-testid={`ai-summary-bullet-${i + 1}`} className="rounded-2xl bg-white/[0.06] border border-white/10 p-5 backdrop-blur-md">
              <div className="flex items-center gap-2 text-amber-300 text-xs font-bold uppercase tracking-wider"><Icon className="h-4 w-4" /> {b.title}</div>
              <p className="mt-2 text-sm leading-relaxed text-amber-50/90">{b.text}</p>
            </div>
          );
        })}
      </div>
      {data?.updating && <div data-testid="ai-summary-updating" className="relative mt-4 inline-flex items-center gap-2 text-[11px] font-semibold text-amber-300"><RefreshCw className="h-3 w-3 animate-spin" /> New feedback received, updating the overview automatically…</div>}
      {data?.generated_at && <div className="relative mt-2 text-[11px] text-amber-50/50">Generated {new Date(data.generated_at).toLocaleString("en-IN")} from {data.based_on} entries{!data.updating && total != null && total !== data.based_on && <span data-testid="ai-summary-stale" className="ml-2 text-amber-300">· New feedback since, regenerate for the latest view</span>}</div>}
    </section>
  );
};
